import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowRight, Eye, Heart, ChatCircle, Books } from "@phosphor-icons/react";
import NewsletterSignup from "@/components/NewsletterSignup";

export default function AuthorSite() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sites/${username}`);
        setData(data);
        if (data?.author?.site_title) {
          document.title = data.author.site_title;
        }
      } catch (e) { setErr("This author hasn't set up a site yet."); }
    })();
  }, [username]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="eyebrow text-muted-foreground">404</div>
        <h1 className="font-serif text-5xl font-black tracking-tight mt-3">{err}</h1>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 link-underline">Back to Inkspot</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-muted-foreground">Loading...</div>;
  const { author, articles } = data;
  const accent = author?.theme?.accent || "#D32F2F";

  return (
    <div>
      {/* MASTHEAD */}
      <header className="border-b border-border" style={{ borderColor: accent }}>
        <div className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
          <div className="eyebrow" style={{ color: accent }}>An Inkspot publication</div>
          <h1 className="font-serif text-4xl sm:text-7xl lg:text-[112px] font-black tracking-tight leading-[0.95] mt-3 break-words">
            {author.site_title || `${author.name}'s Journal`}
          </h1>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {author.profile_picture ? (
                <img src={fileUrl(author.profile_picture)} alt="" className="h-10 w-10 object-cover" />
              ) : (
                <div className="h-10 w-10 bg-secondary grid place-items-center font-serif text-xl">{(author.name || "A")[0]}</div>
              )}
              <div>
                <div className="font-medium">{author.name}</div>
                <div className="text-xs text-muted-foreground font-mono">/{author.username}</div>
              </div>
            </div>
            <Link to={`/${author.username}/about`} className="link-underline text-sm" data-testid="author-about-link">
              About the author
            </Link>
          </div>
          {author.site_tagline ? (
            <p className="mt-8 max-w-2xl text-lg italic font-serif text-muted-foreground border-l-2 pl-4" style={{ borderColor: accent }}>
              "{author.site_tagline}"
            </p>
          ) : null}
        </div>
      </header>

      {/* SERIES */}
      {data.series?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pt-12">
          <div className="eyebrow text-muted-foreground mb-8 flex items-center gap-2">
            <Books size={16} /> Collections & Series
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.series.map((s) => (
              <Link key={s.id} to={`/${author.username}/series/${s.slug}`} className="group block border border-border bg-card p-4 lift-card">
                <div className="aspect-[16/10] bg-secondary mb-4 overflow-hidden border border-border">
                  {s.cover_image ? (
                    <img src={fileUrl(s.cover_image)} alt="" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                  ) : (
                    <div className="w-full h-full grid place-items-center opacity-10"><Books size={40} /></div>
                  )}
                </div>
                <div className="text-[10px] eyebrow text-muted-foreground mb-1">{s.articles_count} Chapters</div>
                <h3 className="font-serif text-lg font-bold group-hover:underline underline-offset-4">{s.title}</h3>
              </Link>
            ))}
          </div>
          <div className="border-b border-border mt-16" />
        </section>
      )}

      {/* ARTICLES */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        {articles.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">No articles published yet.</div>
        ) : (
          <>
            {/* Featured / first article */}
            {articles[0] && (
              <Link to={`/${author.username}/${articles[0].slug}`} className="block group mb-16" data-testid={`article-card-${articles[0].slug}`}>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="grid grid-cols-12 gap-8 items-center">
                  <div className="col-span-12 md:col-span-7 relative">
                    {articles[0].cover_image ? (
                      <div className="aspect-[5/4] border border-border overflow-hidden">
                        <img src={fileUrl(articles[0].cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                      </div>
                    ) : (
                      <div className="aspect-[5/4] bg-secondary border border-border" />
                    )}
                    {articles[0].category && (
                      <div className="absolute top-4 left-4 bg-orange-600 text-white text-[10px] font-black uppercase px-3 py-1.5 shadow-lg z-10 tracking-widest">
                        {articles[0].category.name}
                      </div>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <div className="eyebrow text-muted-foreground">{new Date(articles[0].created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
                    <h2 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-3 leading-[1.05] group-hover:underline underline-offset-4 decoration-2">
                      {articles[0].title}
                    </h2>
                    <p className="mt-4 text-muted-foreground leading-relaxed">{articles[0].excerpt || "Read the full essay →"}</p>
                    <div className="mt-5 flex items-center gap-5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Eye size={14} /> {articles[0].views}</span>
                      <span className="flex items-center gap-1"><Heart size={14} /> {articles[0].likes_count}</span>
                      <span className="flex items-center gap-1"><ChatCircle size={14} /> {articles[0].comments_count}</span>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium link-underline">
                      Read essay <ArrowRight size={14} weight="bold" />
                    </div>
                  </div>
                </motion.div>
              </Link>
            )}

            {articles.length > 1 && (
              <>
                <div className="border-t border-border my-12" />
                <div className="eyebrow text-muted-foreground mb-8">More from {author.name}</div>
                <div className="grid grid-cols-12 gap-8">
                  {articles.slice(1).map((a, i) => (
                    <motion.div
                      key={a.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      className="col-span-12 md:col-span-6 lg:col-span-4"
                    >
                      <Link to={`/${author.username}/${a.slug}`} className="block group" data-testid={`article-card-${a.slug}`}>
                        <div className="relative">
                          {a.cover_image ? (
                            <div className="aspect-[4/3] border border-border overflow-hidden">
                              <img src={fileUrl(a.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                            </div>
                          ) : (
                            <div className="aspect-[4/3] bg-secondary border border-border" />
                          )}
                          {a.category && (
                            <div className="absolute top-3 left-3 bg-orange-600 text-white text-[9px] font-black uppercase px-2 py-1 shadow-lg z-10 tracking-widest">
                              {a.category.name}
                            </div>
                          )}
                        </div>
                        <div className="eyebrow text-muted-foreground mt-4">
                          {new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </div>
                        <h3 className="font-serif text-2xl font-bold tracking-tight mt-2 leading-tight group-hover:underline underline-offset-4 decoration-1">{a.title}</h3>
                        {a.excerpt ? <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p> : null}
                        <div className="mt-4 flex items-center gap-4 text-[10px] text-muted-foreground font-mono">
                          <span className="flex items-center gap-1"><Eye size={12} /> {a.views}</span>
                          <span className="flex items-center gap-1"><Heart size={12} /> {a.likes_count}</span>
                          <span className="flex items-center gap-1"><ChatCircle size={12} /> {a.comments_count}</span>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6">
        <NewsletterSignup username={author.username} accent={accent} />
      </section>

      <footer className="border-t border-border py-10 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
        <div>Published with <Link to="/" className="link-underline text-foreground">Inkspot</Link></div>
        <div className="flex items-center gap-4">
          <Link to="/contact" className="hover:text-foreground">Contact</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
          <Link to="/explore" className="hover:text-foreground">Explore</Link>
        </div>
      </footer>
    </div>
  );
}
